import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getDictionaryItemsByCulture } from 'axios/dictionaryApi';
import { getOrderStatusCounters, getOrderStatusStatuses } from 'axios/orderStatusApi';
import SidebarMenuItem from 'components/Navigation/Sidebar/SidebarMenuItem/SidebarMenuItem';
import useApi from 'hooks/useApi';
import { Dictionary } from 'lodash';
import { OrderStatusCounters } from 'models/api/orderStatus';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useNotifyContext } from 'providers/NotifyProvider';
import { useSidebarContext } from 'providers/SidebarProvider';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiFillEdit } from 'react-icons/ai';
import { Menu, SubMenu } from 'react-pro-sidebar';
import { useQuery, useQueryClient } from 'react-query';
import { useLocation, useSearchParams } from 'react-router-dom';
import { AccordionBody, AccordionHeader, AccordionItem, Spinner } from 'reactstrap';
import { getEndOfTheDay, getStartOfTheDay } from 'utils/dataTime';
import '../Sidebar/Sidebar.scss';
import './OrderStatusesSidebar.scss';
import OrderStatusesSidebarMenuItem from './OrderStatusesSidebarMenuItem';

type StatusGroup = {
    name: string;
    icon: IconProp;
};

type OrderStatusCountersSidebarProps = OrderStatusCounters & {
    open?: boolean;
    translations?: string[];
};

const defaultDates = {
    sd: getStartOfTheDay(),
    ed: getEndOfTheDay(),
};

export const OrderStatusesMenu = ({ menuClass }: { menuClass?: string }) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const { cultures } = useGeneralSettingsContext();
    const { filter: sidebarFilter, setCurrentTab } = useSidebarContext();

    const [statusGroups, setStatusGroups] = useState<StatusGroup[]>([]);
    const [statuses, setStatuses] = useState<OrderStatusCountersSidebarProps[]>([]);
    const [counters, setCounters] = useState<Dictionary<number>>({});
    const [updatedTime, setUpdatedTime] = useState(1);

    const api = useApi();
    const queryClient = useQueryClient();
    const { addSubscription, removeSubscription, showBrowserNotification } = useNotifyContext();

    useEffect(() => {
        addSubscription({
            key: 'onOrderChange',
            type: 'OrderStatusChange',
            callback: (message) => {
                const info = JSON.parse(message);
                const time = new Date().getTime() / 1000;

                if (
                    importantStatuses.some((x) => x.code === info.Status) ||
                    time - updatedTime > 59
                ) {
                    setUpdatedTime(time);
                    queryClient.invalidateQueries(['orderCounters']);
                    queryClient.invalidateQueries([['order']]);

                    if (info.Status === 'manualchecks') {
                        showBrowserNotification(t('BackOfficeOrder') + ' #' + info.OrderId, {
                            body: t('BackOfficeNewOrderMsg'),
                            dir: 'auto',
                        });
                    } else if (info.Status === 'checkdocs') {
                        showBrowserNotification(t('BackOfficeOrder') + ' #' + info.OrderId, {
                            body: t('BackOfficeCheckDocsMsg'),
                            dir: 'auto',
                        });
                    }
                }
            },
        });

        addSubscription({
            key: 'onOrderChange',
            type: 'OrderOperatorChange',
            callback: (message) => {
                message !== '@(((CustomPrincipal)User).UserInfo.Id)' &&
                    queryClient.invalidateQueries(['orderCounters']);
                queryClient.invalidateQueries([['order']]);
            },
        });

        return () => {
            removeSubscription('onOrderChange');
        };
    }, [updatedTime]);

    const isOrderWorkplace = useMemo(() => pathname === '/order', [pathname]);

    const searchOrderStatuses = useMemo(
        () => isOrderWorkplace && searchParams.get('orderStatuses'),
        [isOrderWorkplace, searchParams]
    );

    useEffect(() => {
        isOrderWorkplace && setCurrentTab(searchOrderStatuses ? searchOrderStatuses : 'status-all');
    }, [isOrderWorkplace, searchOrderStatuses]);

    const filter = useMemo(
        () => ({
            sd: (isOrderWorkplace && searchParams.get('sd')) || defaultDates.sd,
            ed: (isOrderWorkplace && searchParams.get('ed')) || defaultDates.ed,
        }),
        [isOrderWorkplace, searchParams]
    );

    const importantStatuses = useMemo(() => statuses.filter((x) => x.isImportant), [statuses]);

    const { isFetching } = useQuery(
        'orderStatusSections',
        () => getDictionaryItemsByCulture(api, 'MenuSection'),
        {
            onSuccess: (data) => {
                const groups = data.map((x) => {
                    const icon =
                        x.props?.find((p) => p.code === 'MenuSectionIcon')?.value ??
                        'fass_pencil-square';

                    const statusGroup: StatusGroup = {
                        name: x.code,
                        icon: icon.split('_') as IconProp,
                    };

                    return statusGroup;
                });

                setStatusGroups(groups);
            },
            enabled: !statusGroups.length,
        }
    );

    const { isFetching: statusesFetching } = useQuery(
        ['orderStatuses'],
        () => getOrderStatusStatuses(api),
        {
            onSuccess: (data) => {
                setStatuses(
                    data.map((item) => ({
                        ...item,
                        open: item.code === searchOrderStatuses,
                        translations: cultures.map((x) =>
                            t(`DictItem${item.translateCode}`, { lng: x.code })?.toLowerCase()
                        ),
                    }))
                );
            },
        }
    );

    useQuery(['orderCounters', filter], () => getOrderStatusCounters(api, filter), {
        onSuccess: (data) => data && setCounters(data),
    });

    const sortedMenu = useMemo(
        () =>
            statuses.sort((a: OrderStatusCounters, b: OrderStatusCounters) => {
                const countA = counters?.[a.code] ?? 0;
                const countB = counters?.[b.code] ?? 0;
                const menuSortingA = a.menuSorting ?? 0;
                const menuSortingB = b.menuSorting ?? 0;

                if (a.isImportant && b.isImportant) {
                    if (countA > 0 && countB > 0) {
                        return menuSortingA > menuSortingB ? 1 : -1;
                    }

                    if (countA === 0 && countB > 0) {
                        return 1;
                    }

                    if (countA > 0 && countB === 0) {
                        return -1;
                    }

                    if (countA === 0 && countB === 0) {
                        return menuSortingA > menuSortingB ? 1 : -1;
                    }
                }

                if (a.isImportant && !b.isImportant) {
                    return -1;
                }

                if (!a.isImportant && b.isImportant) {
                    return 1;
                }

                return menuSortingA > menuSortingB ? 1 : -1;
            }),
        [counters, statuses]
    );

    const filteredMenu = useMemo(
        () =>
            sidebarFilter
                ? sortedMenu.filter(
                      (x) =>
                          !sidebarFilter ||
                          x.translations?.find((item) => item.includes(sidebarFilter.toLowerCase()))
                  )
                : sortedMenu,
        [sortedMenu, sidebarFilter]
    );

    const getCategoryItem = useCallback(
        ({ name, icon }: StatusGroup) => {
            const statuses = filteredMenu.filter((x) => x.menuSection === name);

            return (
                statuses.length && (
                    <SubMenu
                        style={{
                            direction: 'initial',
                            marginBottom: '2px',
                        }}
                        key={name}
                        className={'order-status-menu-group'}
                        title={t(`BackOffice${name}StatusGroup`)}
                        icon={<FontAwesomeIcon icon={icon} className="menu-icon" />}
                        defaultOpen={!!sidebarFilter || statuses.some((x) => x.open)}
                        placeholder={undefined}
                        onPointerEnterCapture={() => undefined}
                        onPointerLeaveCapture={() => undefined}
                    >
                        {statuses.map(({ isImportant, code, icon, translateCode }) => (
                            <OrderStatusesSidebarMenuItem
                                key={code}
                                code={code}
                                translateCode={'DictItem' + translateCode}
                                isImportant={isImportant}
                                icon={icon}
                                counters={counters}
                            />
                        ))}
                    </SubMenu>
                )
            );
        },
        [filteredMenu, counters]
    );

    if (sidebarFilter && !filteredMenu.length) {
        return <></>;
    }

    return (
        <AccordionItem>
            <AccordionHeader tag="div" targetId="orders">
                <Menu iconShape="square">
                    <SidebarMenuItem
                        icon={<AiFillEdit />}
                        id="orders"
                        value="BackOfficeOrders"
                        isVisiblePin={false}
                    />
                </Menu>
            </AccordionHeader>
            <AccordionBody accordionId="orders">
                {isFetching || statusesFetching ? (
                    <Spinner className="ms-4" />
                ) : (
                    <Menu iconShape="square" className={menuClass}>
                        <OrderStatusesSidebarMenuItem
                            key="status-all"
                            code="status-all"
                            translateCode={`BackOfficeOrderStatusAll`}
                            isImportant={false}
                            icon="fa_add"
                            countVisible={false}
                        />
                        {statusGroups.map((prop) => getCategoryItem(prop))}
                    </Menu>
                )}
            </AccordionBody>
        </AccordionItem>
    );
};
